import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Button, AppBar, Toolbar, Grid, Chip,
  Divider, Container, Avatar, Menu, MenuItem, IconButton, Tooltip,
} from "@mui/material";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InventoryOutlinedIcon  from "@mui/icons-material/InventoryOutlined";
import BarChartOutlinedIcon   from "@mui/icons-material/BarChartOutlined";
import LoginOutlinedIcon      from "@mui/icons-material/LoginOutlined";
import LogoutIcon             from "@mui/icons-material/Logout";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import EmailOutlinedIcon      from "@mui/icons-material/EmailOutlined";
import LanguageOutlinedIcon   from "@mui/icons-material/LanguageOutlined";
import PhoneOutlinedIcon      from "@mui/icons-material/PhoneOutlined";
import ArrowForwardIcon       from "@mui/icons-material/ArrowForward";
import DashboardOutlinedIcon  from "@mui/icons-material/DashboardOutlined";
import DarkModeOutlinedIcon   from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon  from "@mui/icons-material/LightModeOutlined";

const light = {
  bg:           "#fafafa",
  bgCard:       "#ffffff",
  bgSection:    "#ffffff",
  bgDark:       "#111827",
  bgDarkCard:   "rgba(255,255,255,0.04)",
  border:       "#e5e7eb",
  borderDark:   "rgba(255,255,255,0.08)",
  text1:        "#111827",
  text2:        "#6b7280",
  text3:        "#374151",
  textDark1:    "#f9fafb",
  textDark2:    "rgba(255,255,255,0.45)",
  textDark3:    "#9ca3af",
  textDark4:    "#4b5563",
  chipBg:       "#f3f4f6",
  chipColor:    "#374151",
  chipDarkBg:   "rgba(255,255,255,0.08)",
  chipDarkColor:"#9ca3af",
  navbarBg:     "rgba(255,255,255,0.95)",
  navbarColor:  "#111827",
  navHover:     "#f3f4f6",
  heroDot:      "#e5e7eb",
  footerBg:     "#111827",
  footerText:   "#6b7280",
  footerText2:  "#4b5563",
  faqBg:        "#ffffff",
  faqOpen:      "#f9fafb",
  ctaBg:        "#f9fafb",
  btnDark:      "#111827",
  btnDarkHover: "#1f2937",
  toggleBg:     "#f3f4f6",
  toggleColor:  "#374151",
};

const dark = {
  bg:           "#0f1117",
  bgCard:       "#1a1d27",
  bgSection:    "#13161f",
  bgDark:       "#0a0c13",
  bgDarkCard:   "rgba(255,255,255,0.03)",
  border:       "#2a2d3a",
  borderDark:   "rgba(255,255,255,0.06)",
  text1:        "#f1f5f9",
  text2:        "#94a3b8",
  text3:        "#cbd5e1",
  textDark1:    "#f1f5f9",
  textDark2:    "rgba(255,255,255,0.45)",
  textDark3:    "#9ca3af",
  textDark4:    "#4b5563",
  chipBg:       "#1e2130",
  chipColor:    "#94a3b8",
  chipDarkBg:   "rgba(255,255,255,0.06)",
  chipDarkColor:"#9ca3af",
  navbarBg:     "rgba(15,17,23,0.95)",
  navbarColor:  "#f1f5f9",
  navHover:     "#1e2130",
  heroDot:      "#2a2d3a",
  footerBg:     "#0a0c13",
  footerText:   "#4b5563",
  footerText2:  "#374151",
  faqBg:        "#13161f",
  faqOpen:      "#1a1d27",
  ctaBg:        "#1a1d27",
  btnDark:      "#f1f5f9",
  btnDarkHover: "#e2e8f0",
  toggleBg:     "#1e2130",
  toggleColor:  "#94a3b8",
};

function FaqItem({ question, answer, t }) {
  const [open, setOpen] = useState(false);
  return (
    <Box
      onClick={() => setOpen(!open)}
      sx={{
        border: `1px solid ${t.border}`, borderRadius: "10px", overflow: "hidden",
        cursor: "pointer", transition: "border-color 0.15s, background 0.2s",
        "&:hover": { borderColor: open ? t.border : t.text2 },
        backgroundColor: open ? t.faqOpen : t.faqBg,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2.5, py: 1.5 }}>
        <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: t.text1, pr: 2 }}>
          {question}
        </Typography>
        <Box sx={{
          width: 20, height: 20, borderRadius: "5px",
          backgroundColor: open ? t.text1 : t.chipBg,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0, transition: "all 0.2s",
        }}>
          <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: open ? t.bgCard : t.text2, lineHeight: 1, mt: "-1px" }}>
            {open ? "−" : "+"}
          </Typography>
        </Box>
      </Box>
      {open && (
        <Box sx={{ px: 2.5, pb: 2 }}>
          <Typography sx={{ fontSize: "0.78rem", color: t.text2, lineHeight: 1.75 }}>
            {answer}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default function LandingPage() {
  const navigate  = useNavigate();
  const [isDark, setIsDark] = useState(false);
  const t = isDark ? dark : light;

  const user = (() => {
    try { return JSON.parse(localStorage.getItem("user")); }
    catch { return null; }
  })();
  const token        = localStorage.getItem("token");
  const isLoggedIn   = !!(token && user?.role?.name);
  const isAdmin      = user?.role?.name === "admin";
  const userInitials = user?.name
    ? user.name.split(" ").slice(0, 2).map((n) => n[0]).join("").toUpperCase()
    : "U";

  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu  = (e) => setAnchorEl(e.currentTarget);
  const closeMenu = ()  => setAnchorEl(null);

  const handleLogout = () => {
    closeMenu();
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    window.location.reload();
  };

  const handleDashboard = () => {
    closeMenu();
    navigate(isAdmin ? "/admin" : "/user");
  };

  const features = [
    {
      icon: <AssignmentOutlinedIcon sx={{ fontSize: 20 }} />,
      title: "Pengajuan Digital Terstruktur",
      desc: "Formulir pengajuan dirancang secara sistematis untuk memudahkan pencatatan kebutuhan barang beserta spesifikasi, estimasi anggaran, dan tingkat urgensi secara lengkap dan akurat.",
      color: "#1677ff", bg: isDark ? "#0d1a2e" : "#f0f7ff", border: isDark ? "#1a3a5c" : "#dbeafe",
    },
    {
      icon: <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />,
      title: "Alur Persetujuan Transparan",
      desc: "Setiap pengajuan diproses melalui alur persetujuan yang jelas dan terdokumentasi. Notifikasi status dikirimkan secara otomatis kepada pemohon pada setiap tahap pemrosesan.",
      color: "#059669", bg: isDark ? "#0a1f18" : "#f0fdf4", border: isDark ? "#134e35" : "#d1fae5",
    },
    {
      icon: <InventoryOutlinedIcon sx={{ fontSize: 20 }} />,
      title: "Manajemen Inventaris Terpadu",
      desc: "Seluruh aset tercatat secara digital mencakup kondisi barang, lokasi penempatan, dan riwayat perpindahan. Data inventaris selalu mutakhir dan dapat diakses kapan saja.",
      color: "#d97706", bg: isDark ? "#1f1608" : "#fffbeb", border: isDark ? "#4a3010" : "#fde68a",
    },
    {
      icon: <BarChartOutlinedIcon sx={{ fontSize: 20 }} />,
      title: "Pelaporan & Analitik Data",
      desc: "Dashboard menyajikan ringkasan pengajuan, realisasi pengadaan, dan kondisi aset dalam visualisasi yang informatif untuk mendukung pengambilan keputusan berbasis data.",
      color: "#7c3aed", bg: isDark ? "#150d2a" : "#f5f3ff", border: isDark ? "#2e1a5c" : "#ede9fe",
    },
  ];

  const faqs = [
    {
      q: "Apa itu BAS-LPKIA?",
      a: "BAS-LPKIA adalah sistem informasi manajemen pengadaan sarana prasarana dan inventaris aset milik Institut Digital Ekonomi LPKIA. Sistem ini dirancang untuk menggantikan proses konvensional dengan alur kerja digital yang terstruktur, transparan, dan dapat dipantau secara real-time.",
    },
    {
      q: "Siapa yang dapat menggunakan sistem ini?",
      a: "Sistem ini diperuntukkan bagi staf, tenaga kependidikan, dan pimpinan unit kerja di lingkungan Institut Digital Ekonomi LPKIA yang memiliki akun yang telah diverifikasi oleh administrator.",
    },
    {
      q: "Bagaimana cara mendapatkan akses ke sistem?",
      a: "Akses dapat diperoleh dengan mendaftar langsung melalui halaman login. Tersedia dua cara masuk menggunakan akun Google atau mendaftar dengan email dan kata sandi. Setelah mendaftar, akun akan aktif dan siap digunakan sesuai peran yang ditetapkan.",
    },
    {
      q: "Berapa lama proses persetujuan pengajuan berlangsung?",
      a: "Durasi persetujuan mengikuti kebijakan dan prosedur internal yang berlaku. Sistem akan mengirimkan notifikasi otomatis kepada pemohon setiap kali terdapat pembaruan status pengajuan.",
    },
    {
      q: "Apakah satu pengajuan dapat mencakup lebih dari satu jenis barang?",
      a: "Ya. Dalam satu formulir pengajuan, pengguna dapat mencantumkan beberapa item barang sekaligus, lengkap dengan spesifikasi teknis, jumlah kebutuhan, dan estimasi harga satuan masing-masing item.",
    },
    {
      q: "Apa yang terjadi setelah pengajuan disetujui?",
      a: "Pengajuan yang telah disetujui akan diproses sesuai alur pengadaan yang berlaku. Barang yang diterima akan dicatatkan secara otomatis ke dalam sistem inventaris, termasuk lokasi penempatan dan kondisi awal barang.",
    },
    {
      q: "Apakah pengajuan yang sudah dikirim bisa dibatalkan?",
      a: "Pengajuan yang masih berstatus 'Menunggu' dapat dihapus oleh pemohon melalui sistem. Pengajuan yang telah memasuki tahap pemrosesan tidak dapat diubah secara mandiri — silakan menghubungi administrator untuk penanganan lebih lanjut.",
    },
    {
      q: "Bagaimana keamanan data pada sistem ini dijamin?",
      a: "Seluruh data tersimpan di server institusi dan hanya dapat diakses oleh pengguna yang memiliki akun terverifikasi. Setiap aktivitas pengguna dicatat dalam log sistem untuk keperluan pengawasan dan audit internal.",
    },
  ];

  const trans = { transition: "background-color 0.25s ease, color 0.25s ease, border-color 0.25s ease" };

  return (
    <Box sx={{ backgroundColor: t.bg, minHeight: "100vh", ...trans }}>

      {/* ── NAVBAR ── */}
      <AppBar position="sticky" elevation={0} sx={{
        backgroundColor: t.navbarBg, backdropFilter: "blur(16px)",
        borderBottom: `1px solid ${t.border}`, color: t.navbarColor, ...trans,
      }}>
        <Toolbar sx={{ maxWidth: "1100px", width: "100%", mx: "auto", px: { xs: 2, md: 3 }, minHeight: "56px !important" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexGrow: 1 }}>
            <Box component="img" src="/lpkia-logo2.png" alt="LPKIA" sx={{ height: 32, width: "auto", objectFit: "contain" }} />
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: "0.82rem", color: t.text1, lineHeight: 1.2, letterSpacing: "-0.01em", ...trans }}>
                BAS-LPKIA
              </Typography>
              <Typography sx={{ fontSize: "0.58rem", color: t.text2, lineHeight: 1.2, ...trans }}>
                Sistem Manajemen Pengadaan & Aset
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 0.5, mr: 2 }}>
            {[
              { label: "Fitur",      id: "fitur"      },
              { label: "Cara Kerja", id: "cara-kerja" },
              { label: "FAQ",        id: "faq"        },
            ].map((n) => (
              <Box key={n.label}
                onClick={() => document.getElementById(n.id)?.scrollIntoView({ behavior: "smooth" })}
                sx={{
                  px: 1.2, py: 0.6, borderRadius: "6px",
                  fontSize: "0.78rem", fontWeight: 500, color: t.text2,
                  cursor: "pointer", ...trans,
                  "&:hover": { backgroundColor: t.navHover, color: t.text1 },
                }}>
                {n.label}
              </Box>
            ))}
          </Box>

          <Tooltip title={isDark ? "Mode Terang" : "Mode Gelap"} arrow>
            <IconButton
              onClick={() => setIsDark(!isDark)}
              size="small"
              sx={{
                mr: 1.5, backgroundColor: t.toggleBg, color: t.toggleColor,
                width: 30, height: 30, borderRadius: "7px",
                border: `1px solid ${t.border}`, ...trans,
                "&:hover": { backgroundColor: t.navHover },
              }}
            >
              {isDark
                ? <LightModeOutlinedIcon sx={{ fontSize: 15 }} />
                : <DarkModeOutlinedIcon  sx={{ fontSize: 15 }} />}
            </IconButton>
          </Tooltip>

          {isLoggedIn ? (
            <>
              <Avatar onClick={openMenu} sx={{
                width: 30, height: 30, fontSize: "0.68rem", fontWeight: 800,
                background: "linear-gradient(135deg, #1677ff 0%, #7c3aed 100%)",
                cursor: "pointer", transition: "transform 0.15s, box-shadow 0.15s",
                "&:hover": { transform: "scale(1.06)", boxShadow: "0 4px 14px rgba(22,119,255,0.35)" },
              }}>
                {userInitials}
              </Avatar>
              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                slotProps={{
                  paper: {
                    elevation: 0,
                    sx: {
                      mt: 1.5, minWidth: 180, borderRadius: "10px",
                      border: `1px solid ${t.border}`,
                      boxShadow: isDark ? "0 8px 32px rgba(0,0,0,0.4)" : "0 8px 32px rgba(0,0,0,0.1)",
                      backgroundColor: t.bgCard, overflow: "hidden", ...trans,
                    }
                  }
                }}>
                <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${t.border}` }}>
                  <Typography sx={{ fontWeight: 800, fontSize: "0.8rem", color: t.text1, letterSpacing: "-0.02em", ...trans }}>
                    {user?.name || "Pengguna"}
                  </Typography>
                  <Typography sx={{ fontSize: "0.68rem", color: t.text2, mt: 0.2, ...trans }}>
                    {user?.unit?.name || user?.email || "—"}
                  </Typography>
                </Box>
                <Box sx={{ p: 0.5 }}>
                  <MenuItem onClick={handleDashboard} sx={{ borderRadius: "7px", gap: 1.2, fontSize: "0.8rem", py: 0.8, px: 1.2, color: t.text1, "&:hover": { backgroundColor: t.navHover } }}>
                    <DashboardOutlinedIcon sx={{ fontSize: 16, color: t.text2 }} />
                    Dashboard
                  </MenuItem>
                </Box>
                <Divider sx={{ borderColor: t.border }} />
                <Box sx={{ p: 0.5 }}>
                  <MenuItem onClick={handleLogout} sx={{ borderRadius: "7px", gap: 1.2, fontSize: "0.8rem", py: 0.8, px: 1.2, color: "#ef4444", "&:hover": { backgroundColor: isDark ? "#1a0a0a" : "#fff5f5" } }}>
                    <LogoutIcon sx={{ fontSize: 16, color: "#ef4444" }} />
                    Keluar
                  </MenuItem>
                </Box>
              </Menu>
            </>
          ) : (
            <Button variant="contained" startIcon={<LoginOutlinedIcon sx={{ fontSize: 14 }} />}
              onClick={() => navigate("/login")}
              sx={{
                borderRadius: "7px", textTransform: "none", fontSize: "0.78rem", fontWeight: 600,
                backgroundColor: t.btnDark, px: 2, py: 0.8, boxShadow: "none", color: isDark ? "#0f1117" : "#fff",
                "&:hover": { backgroundColor: t.btnDarkHover, boxShadow: "none" },
              }}>
              Masuk
            </Button>
          )}
        </Toolbar>
      </AppBar>

      {/* ── HERO ── */}
      <Box sx={{
        backgroundColor: t.bgSection, borderBottom: `1px solid ${t.border}`,
        py: { xs: 6, md: 10 }, position: "relative", overflow: "hidden", ...trans,
      }}>
        <Box sx={{
          position: "absolute", inset: 0, zIndex: 0,
          backgroundImage: `radial-gradient(${t.heroDot} 1px, transparent 1px)`,
          backgroundSize: "24px 24px", opacity: 0.5,
        }} />
        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1, textAlign: "center" }}>
          <Chip label="Sistem Manajemen Pengadaan & Aset · Institut Digital Ekonomi LPKIA" size="small"
            sx={{
              backgroundColor: t.chipBg, color: t.chipColor,
              border: `1px solid ${t.border}`, fontWeight: 500, fontSize: "0.66rem",
              mb: 3, height: 24, borderRadius: "6px", ...trans,
            }} />
          <Typography variant="h1" sx={{
            fontSize: { xs: "1.7rem", md: "2.8rem" }, fontWeight: 800, color: t.text1,
            lineHeight: 1.15, letterSpacing: "-0.03em", mb: 2.5, ...trans,
          }}>
            Pengadaan Aset Institusi{" "}
            <Box component="span" sx={{
              background: "linear-gradient(135deg, #1677ff 0%, #7c3aed 100%)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            }}>
              Lebih Terkelola
            </Box>
          </Typography>
          <Typography sx={{
            fontSize: { xs: "0.85rem", md: "0.95rem" }, color: t.text2,
            lineHeight: 1.8, mb: 4, maxWidth: "560px", mx: "auto", ...trans,
          }}>
            BAS-LPKIA merupakan sistem informasi manajemen pengadaan sarana prasarana dan
            inventaris aset institusi yang dirancang untuk menggantikan proses konvensional
            dengan alur kerja digital yang terstruktur, akuntabel, dan dapat dipantau
            secara real-time oleh seluruh pemangku kepentingan.
          </Typography>
          <Box sx={{ display: "flex", gap: 1.2, justifyContent: "center", flexWrap: "wrap" }}>
            <Button variant="contained" size="medium" endIcon={<ArrowForwardIcon sx={{ fontSize: 15 }} />}
              onClick={() => navigate("/login")}
              sx={{
                borderRadius: "7px", textTransform: "none", fontSize: "0.85rem", fontWeight: 700,
                px: 3, py: 1.1, backgroundColor: t.btnDark, boxShadow: "none",
                color: isDark ? "#0f1117" : "#fff",
                "&:hover": { backgroundColor: t.btnDarkHover, boxShadow: "0 4px 16px rgba(0,0,0,0.15)", transform: "translateY(-1px)" },
                transition: "all 0.2s",
              }}>
              Akses Sistem
            </Button>
            <Button variant="outlined" size="medium"
              onClick={() => document.getElementById("cara-kerja")?.scrollIntoView({ behavior: "smooth" })}
              sx={{
                borderRadius: "7px", textTransform: "none", fontSize: "0.85rem", fontWeight: 600,
                px: 3, py: 1.1, borderColor: t.border, color: t.text3,
                "&:hover": { borderColor: t.text2, backgroundColor: t.navHover },
              }}>
              Pelajari Alur Kerja
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ── FITUR ── */}
      <Box id="fitur" sx={{ py: { xs: 5, md: 8 }, backgroundColor: t.bgSection, borderBottom: `1px solid ${t.border}`, ...trans }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: 5 }}>
            <Chip label="Fitur Sistem" size="small"
              sx={{ backgroundColor: t.chipBg, color: t.chipColor, fontWeight: 600, fontSize: "0.64rem",
                mb: 1.5, borderRadius: "6px", textTransform: "uppercase", letterSpacing: "0.08em", ...trans }} />
            <Typography sx={{ fontSize: { xs: "1.25rem", md: "1.65rem" }, fontWeight: 800, color: t.text1, letterSpacing: "-0.03em", mb: 1, ...trans }}>
              Satu Platform untuk Seluruh Siklus Pengadaan
            </Typography>
            <Typography sx={{ fontSize: "0.82rem", color: t.text2, maxWidth: 500, mx: "auto", lineHeight: 1.75, ...trans }}>
              Dirancang untuk memenuhi kebutuhan pengelolaan aset dan pengadaan barang secara menyeluruh —
              dari pencatatan permintaan hingga pelaporan realisasi dalam satu sistem yang terintegrasi.
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {features.map((f, i) => (
              <Grid key={i} size={{ xs: 12, sm: 6, md: 3 }}>
                <Box sx={{
                  backgroundColor: t.bgCard, border: `1px solid ${t.border}`, borderRadius: "10px", p: 2.5,
                  height: "100%", transition: "box-shadow 0.2s, transform 0.2s, background-color 0.25s, border-color 0.25s",
                  "&:hover": { boxShadow: isDark ? "0 8px 32px rgba(0,0,0,0.4)" : "0 8px 32px rgba(0,0,0,0.08)", transform: "translateY(-2px)", borderColor: f.border },
                }}>
                  <Box sx={{ width: 38, height: 38, borderRadius: "8px", backgroundColor: f.bg,
                    border: `1px solid ${f.border}`, display: "flex", alignItems: "center",
                    justifyContent: "center", color: f.color, mb: 1.5, ...trans }}>
                    {f.icon}
                  </Box>
                  <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: t.text1, mb: 0.8, ...trans }}>{f.title}</Typography>
                  <Typography sx={{ fontSize: "0.76rem", color: t.text2, lineHeight: 1.7, ...trans }}>{f.desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── CARA KERJA ── */}
      <Box id="cara-kerja" sx={{ backgroundColor: t.bgDark, py: { xs: 5, md: 8 }, ...trans }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: 5 }}>
            <Chip label="Alur Kerja Sistem" size="small"
              sx={{ backgroundColor: t.chipDarkBg, color: t.chipDarkColor, fontWeight: 600,
                fontSize: "0.64rem", mb: 1.5, borderRadius: "6px", textTransform: "uppercase", letterSpacing: "0.08em" }} />
            <Typography sx={{ fontSize: { xs: "1.25rem", md: "1.65rem" }, fontWeight: 800, color: t.textDark1, letterSpacing: "-0.03em", mb: 1 }}>
              Proses Pengadaan yang Efisien dalam 3 Tahap
            </Typography>
            <Typography sx={{ fontSize: "0.82rem", color: t.textDark2, maxWidth: 460, mx: "auto", lineHeight: 1.75 }}>
              Alur kerja yang sebelumnya memerlukan koordinasi manual kini dapat dikelola secara digital
              dengan jejak dokumen yang lengkap dan teraudit.
            </Typography>
          </Box>
          <Grid container spacing={2.5} justifyContent="center">
            {[
              {
                num: "01",
                title: "Autentikasi & Verifikasi Akses",
                desc: "Pengguna masuk menggunakan kredensial akun institusi yang telah terdaftar dan diverifikasi. Hak akses diberikan sesuai peran — pemohon atau administrator sistem.",
              },
              {
                num: "02",
                title: "Penyusunan & Pengiriman Pengajuan",
                desc: "Pemohon mengisi formulir pengajuan secara digital dengan mencantumkan spesifikasi barang, jumlah kebutuhan, estimasi anggaran, dan tingkat prioritas secara terperinci.",
              },
              {
                num: "03",
                title: "Pemantauan Status & Notifikasi",
                desc: "Status pengajuan diperbarui secara real-time dan notifikasi dikirimkan otomatis kepada pemohon pada setiap perubahan tahap, mulai dari penerimaan hingga keputusan akhir.",
              },
            ].map((s, i) => (
              <Grid key={i} size={{ xs: 12, sm: 4 }}>
                <Box sx={{ backgroundColor: t.bgDarkCard, border: `1px solid ${t.borderDark}`, borderRadius: "10px", p: 3, height: "100%" }}>
                  <Typography sx={{ fontSize: "2.2rem", fontWeight: 900, color: t.textDark2, lineHeight: 1, mb: 1.5 }}>
                    {s.num}
                  </Typography>
                  <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: t.textDark1, mb: 0.8 }}>{s.title}</Typography>
                  <Typography sx={{ fontSize: "0.78rem", color: t.textDark2, lineHeight: 1.75 }}>{s.desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── FAQ ── */}
      <Box id="faq" sx={{ backgroundColor: t.faqBg, borderBottom: `1px solid ${t.border}`, py: { xs: 5, md: 8 }, ...trans }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: "center", mb: 5 }}>
            <Chip label="FAQ" size="small"
              sx={{ backgroundColor: t.chipBg, color: t.chipColor, fontWeight: 600, fontSize: "0.64rem",
                mb: 1.5, borderRadius: "6px", textTransform: "uppercase", letterSpacing: "0.08em", ...trans }} />
            <Typography sx={{ fontSize: { xs: "1.25rem", md: "1.65rem" }, fontWeight: 800, color: t.text1, letterSpacing: "-0.03em", mb: 1, ...trans }}>
              Pertanyaan yang Sering Diajukan
            </Typography>
            <Typography sx={{ fontSize: "0.82rem", color: t.text2, maxWidth: 440, mx: "auto", lineHeight: 1.75, ...trans }}>
              Kumpulan jawaban atas pertanyaan umum seputar penggunaan dan kebijakan sistem BAS-LPKIA.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
            {faqs.map((item, i) => (
              <FaqItem key={i} question={item.q} answer={item.a} t={t} />
            ))}
          </Box>
          <Box sx={{ mt: 4, textAlign: "center", p: 2.5, borderRadius: "10px", backgroundColor: t.ctaBg, border: `1px solid ${t.border}`, ...trans }}>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: t.text1, mb: 0.4, ...trans }}>
              Membutuhkan informasi lebih lanjut?
            </Typography>
            <Typography sx={{ fontSize: "0.76rem", color: t.text2, ...trans }}>
              Silakan hubungi tim administrator melalui alamat surel{" "}
              <Box component="span" sx={{ color: "#1677ff", fontWeight: 600 }}>
                sekretariat@lpkia.ac.id
              </Box>{" "}
              atau mengunjungi Bagian Umum & Logistik kampus pada jam kerja.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ── FOOTER ── */}
      <Box sx={{ backgroundColor: t.footerBg, ...trans }}>
        <Container maxWidth="lg">
          <Box sx={{ py: { xs: 4, md: 6 }, display: "flex", gap: 5, flexWrap: "wrap" }}>
            <Box sx={{ flex: "1 1 240px" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
                <Box component="img" src="/lpkia-logo2.png" alt="LPKIA" sx={{ height: 30, width: "auto", objectFit: "contain" }} />
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: "0.82rem", color: "#f9fafb", lineHeight: 1.2 }}>BAS-LPKIA</Typography>
                  <Typography sx={{ fontSize: "0.58rem", color: t.footerText, lineHeight: 1.2 }}>Sistem Manajemen Pengadaan & Aset</Typography>
                </Box>
              </Box>
              <Typography sx={{ fontSize: "0.76rem", color: t.footerText, lineHeight: 1.8, maxWidth: "240px", mb: 2.5 }}>
                Sistem informasi manajemen pengadaan dan inventaris aset untuk mendukung
                tata kelola institusi yang efisien, transparan, dan akuntabel.
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {[
                  { icon: <LocationOnOutlinedIcon sx={{ fontSize: 12 }} />, text: "Jl. Soekarno-Hatta No.456, Bandung 40266" },
                  { icon: <PhoneOutlinedIcon      sx={{ fontSize: 12 }} />, text: "(022) 5201 448" },
                  { icon: <EmailOutlinedIcon      sx={{ fontSize: 12 }} />, text: "sekretariat@lpkia.ac.id" },
                  { icon: <LanguageOutlinedIcon   sx={{ fontSize: 12 }} />, text: "www.lpkia.ac.id" },
                ].map((c, i) => (
                  <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 0.8 }}>
                    <Box sx={{ color: t.footerText2, mt: "2px", flexShrink: 0 }}>{c.icon}</Box>
                    <Typography sx={{ fontSize: "0.72rem", color: t.footerText }}>{c.text}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: { xs: 5, md: 8 }, flexWrap: "wrap",
              flex: "2 1 300px", justifyContent: { xs: "flex-start", md: "flex-end" } }}>
              {[
                { title: "Sistem", links: [
                  { label: "Akses Sistem",   action: () => navigate("/login") },
                  { label: "Fitur Platform", action: () => document.getElementById("fitur")?.scrollIntoView({ behavior: "smooth" }) },
                  { label: "Alur Kerja",     action: () => document.getElementById("cara-kerja")?.scrollIntoView({ behavior: "smooth" }) },
                  { label: "FAQ",            action: () => document.getElementById("faq")?.scrollIntoView({ behavior: "smooth" }) },
                ]},
                { title: "Institusi", links: [
                  { label: "Tentang LPKIA",  action: () => window.open("https://lpkia.ac.id/profil-lpkia/", "_blank") },
                  { label: "Program Studi",  action: () => window.open("https://pmb.lpkia.ac.id/program-studi", "_blank") },
                  { label: "Penerimaan PMB", action: () => window.open("https://pmb.lpkia.ac.id", "_blank") },
                  { label: "lpkia.ac.id",    action: () => window.open("https://lpkia.ac.id", "_blank") },
                ]},
              ].map(({ title, links }) => (
                <Box key={title} sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography sx={{ fontSize: "0.62rem", fontWeight: 700, color: "#f9fafb",
                    textTransform: "uppercase", letterSpacing: "0.1em", mb: 0.5 }}>
                    {title}
                  </Typography>
                  {links.map((link) => (
                    <Typography key={link.label} onClick={link.action} sx={{
                      fontSize: "0.76rem", color: t.footerText, cursor: "pointer",
                      transition: "color 0.15s", "&:hover": { color: "#e5e7eb" },
                    }}>
                      {link.label}
                    </Typography>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
          <Divider sx={{ borderColor: isDark ? "#1a1d27" : "#1f2937" }} />
          <Box sx={{ py: 2, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
            <Typography sx={{ fontSize: "0.68rem", color: t.footerText2 }}>
              © {new Date().getFullYear()} BAS-LPKIA · Designed & Developed by{" "}
              <Box
                component="a"
                href="https://github.com/rbprmna"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: t.footerText2,
                  textDecoration: "none",
                  transition: "color 0.15s",
                  "&:hover": { color: "#e5e7eb" },
                }}
              >
                Robi Permana
              </Box>
            </Typography>
            <Typography sx={{ fontSize: "0.68rem", color: t.footerText2 }}>
              Sistem ini diperuntukkan bagi keperluan internal institusi
            </Typography>
          </Box>
        </Container>
      </Box>

    </Box>
  );
}